//CRN_20251218 - ⚠️ ngOnInit() called for components/directives & not services ⚠️

import { AssistantSearchKind, ChatGptModalTarget } from 'src/app/core/types/chatgpt/types-chatgpt';
import { AssistantSearchRequestDTO, AssistantSearchResultDTO, AssistantSearchResultDTO_EmptyObject, ChatGptAuditEntry, ChatGptModalActionHandlers } from 'src/app/core/interfaces/chatgpt/interfaces-chatgpt';
import { CompanyDTO } from 'src/app/core/models/system/company/company.dto';
import { EncryptionService } from './encryption-service';
import { environment } from 'src/environments/environment';
import { eSimApiPaths, StorageValues } from 'src/app/core/consts';
import { ExceptionsService } from './exceptions-service';
import { HttpClientService } from './http-client-service';
import { IdAndDescriptionDTO } from 'src/app/core/interfaces/general/id-description';
import { Injectable } from '@angular/core';
import { map, Observable, of, switchMap, throwError } from 'rxjs';
import { StringResultDTO } from 'src/app/core/interfaces/general/string-result';
import { Ticket } from 'src/app/core/models/system/support/ticket-hub/ticket';
import { UserDTO } from 'src/app/core/models/system/user/user.dto';

@Injectable({ providedIn: 'root' })

export class ChatGptService {
  
  constructor(
    private encryptionService: EncryptionService,
    private exceptionsService: ExceptionsService,
    private httpClientService: HttpClientService
  ) { }

  //#region OpenAI Direct
    
  public sendMessage(prompt: string): Observable<string> {
    try {
      return this.encryptionService.encrypt_AesServerSideForWeb(prompt)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const promptEnc=encResult.result;
                            
                            return this.httpClientService
                                   ._post<{ message: string }>(environment.eSimApi + eSimApiPaths.ChatGpt, { prompt: promptEnc })
                                   .pipe(map(res => res.message)); 
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  //#endregion

  //#region OpenAI via Azure

  public sendMessageToAzureOpenAi(prompt: string): Observable<string> {
    try {
      return this.encryptionService.encrypt_AesServerSideForWeb(prompt)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const promptEnc=encResult.result;
                            
                            return this.httpClientService
                                   ._post<{ message: string }>(environment.eSimApi +
                                                               eSimApiPaths.ChatGpt + '/' +
                                                               eSimApiPaths.ChatGptViaAzureOpenAi, { question: promptEnc })
                                   .pipe(map(res => res.message)); 
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  //#endregion

  //#region Assistant Search

  public search(query: string, kinds?: AssistantSearchKind[], limitPerKind: number=25): Observable<AssistantSearchResultDTO> {
    try {
      return this.encryptionService.encrypt_AesServerSideForWeb(query ?? '')
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const request: AssistantSearchRequestDTO={
                              kinds       : kinds && kinds.length > 0 ? kinds : undefined,
                              limitPerKind: limitPerKind,
                              queryEnc    : encResult.result
                            };

                            return this.httpClientService
                                   ._post<AssistantSearchResultDTO>(environment.eSimApi
                                                                    + eSimApiPaths.Assistant
                                                                    + '/'
                                                                    + eSimApiPaths.AssistantSearch, request)
                                   .pipe(map(res => this.normaliseSearchResult(res)));
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return throwError(() => e);
    }
  }

  public isSearchTruncated(result: AssistantSearchResultDTO | undefined): boolean {
    try {
      return Object.values(result?.truncated ?? {}).some(x => x===true);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return false;
    }
  }

  private normaliseSearchResult(res: Partial<AssistantSearchResultDTO> | null | undefined): AssistantSearchResultDTO {
    try {
      const empty                                   =AssistantSearchResultDTO_EmptyObject();
      const r    : Partial<AssistantSearchResultDTO>=res ?? {};

      return {
        companies    : Array.isArray(r.companies)     ? r.companies     : empty.companies,
        portalUsers  : Array.isArray(r.portalUsers)   ? r.portalUsers   : empty.portalUsers,
        sims         : Array.isArray(r.sims)          ? r.sims          : empty.sims,
        simUsers     : Array.isArray(r.simUsers)      ? r.simUsers      : empty.simUsers,
        ticketsClosed: Array.isArray(r.ticketsClosed) ? r.ticketsClosed : empty.ticketsClosed,
        ticketsOpen  : Array.isArray(r.ticketsOpen)   ? r.ticketsOpen   : empty.ticketsOpen,
        truncated    : { ...empty.truncated, ...(r.truncated ?? {}) }
      };
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return AssistantSearchResultDTO_EmptyObject();
    }
  }

  //#endregion

  public appendAudit(eventType: string, userId?: number): void {
    try {
      localStorage.removeItem(StorageValues.sureSimAssistantAuditLog);

      const current=this.readAudit(userId);

      current.push({
        eventType: eventType,
        timestamp: new Date().toISOString()
      });

      localStorage.setItem(this.auditStorageKey(userId), JSON.stringify(current.slice(-100)));
    }
    catch(e) {
      //CRN_20260224 - Ignore audit failures
      // this.exceptionsService.handleException(<Error>e);
    }
  }

  private auditStorageKey(userId?: number): string {
    try {
      return `${StorageValues.sureSimAssistantAuditLog_}${userId ?? 0}`;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return '';
    }
  }

  public readAudit(userId?: number): ChatGptAuditEntry[] {
    try {
      const raw   =localStorage.getItem(this.auditStorageKey(userId));
      const parsed=raw ? JSON.parse(raw) : [];

      return Array.isArray(parsed) ? <ChatGptAuditEntry[]>parsed : [];
    }
    catch {
      return [];
    }
  }

  public canExecuteModalAction(modal: ChatGptModalTarget | undefined): boolean {
    try {
      switch (modal) {
        case 'company':
        case 'download':
        case 'manage_sim':
        case 'provision':
        case 'ticket':
        case 'timeline':
        case 'upload':
        case 'user_portal':
        case 'user_sim':
          return true;
        default:
          return false;
      }
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return false;
    }
  }

  public executeModalAction(modal     : ChatGptModalTarget,
                            payload   : any,
                            handlers  : ChatGptModalActionHandlers,
                            onUnknown?: (modal: string) => void): void {
    try {
      const handler=handlers[modal];

      if (handler) {
        handler(payload);
        return;
      }
      
      if (onUnknown)
        onUnknown(modal);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  //#region Resolvers

  public resolveCompanyId(companies: CompanyDTO[], initialCompanyId: number, companyName: string): number {
    try {
      if (initialCompanyId>0)
        return initialCompanyId;
  
      const trimmedName=(companyName ?? '').trim();

      if (!trimmedName)
        return -1;
  
      const matches=this.findMatchingCompanies(companies ?? [], trimmedName);
      const first  =matches[0];

      return Number(first?.id ?? -1);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return -1;
    }
  }

  public resolveUserByName(users: UserDTO[], userName: string): UserDTO | undefined {
    try {
      return this.findMatchingUser(users ?? [], userName ?? '');
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return undefined;
    }
  }

  //#endregion

  //#region Search

  public findMatchingCompanies(companies: CompanyDTO[], companyName: string): CompanyDTO[] {
    try {
      const target=this.normalizeMatchText(companyName);
      const exact =companies.filter(c => this.normalizeMatchText(c.companyName ?? '')===target);

      if (exact.length>0)
        return exact;
        
      return companies.filter(c => this.normalizeMatchText(c.companyName ?? '').includes(target));
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return [];
    }
  }
  
  public findMatchingSimCandidates(candidates: IdAndDescriptionDTO[], simIdentifier: string): IdAndDescriptionDTO[] {
    try {
      const target=this.normalizeMatchText(simIdentifier);
      const exact =candidates.filter(c => this.normalizeMatchText((c.description ?? '').toString())===target);

      if (exact.length>0)
        return exact;
        
      return candidates.filter(c => this.normalizeMatchText((c.description ?? '').toString()).includes(target));
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return [];
    }
  }
  
  public findMatchingTickets(tickets: Ticket[], query: string): Ticket[] {
    try {
      const numericQuery=this.parsePositiveInteger(query);

      if (numericQuery) {
        const exactId=tickets.filter(t => Number(t.id)===numericQuery);

        if (exactId.length>0)
          return exactId;
      }
      
      const target=this.normalizeMatchText(query);
      const exact =tickets.filter(t => {
                     const values=this.ticketMatchValues(t);
                     
                     return values.some(v => this.normalizeMatchText(v)===target);
                   });

      if (exact.length>0)
        return exact;
      
      return tickets.filter(t => {
               const values=this.ticketMatchValues(t);
               
               return values.some(v => this.normalizeMatchText(v).includes(target));
             });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return [];
    }
  }
  
  public findMatchingUser(users: UserDTO[], userName: string): UserDTO | undefined {
    try {
      return this.findMatchingUsers(users, userName)[0];
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return undefined;
    }
  }
  
  public findMatchingUsers(users: UserDTO[], userName: string): UserDTO[] {
    try {
      const target    =(userName ?? '').trim();
      const targetNorm=this.normalizeMatchText(target);

      if (!targetNorm)
        return [];
      
      const exact=users.filter(x => {
                    const fullNameCombined=`${x.firstname ?? ''} ${x.lastname ?? ''}`.trim();
                    const values          =[x.username ?? '', x.email ?? '', x.fullname ?? '', fullNameCombined];
                    
                    return values.some(v => this.normalizeMatchText(v)===targetNorm);
                  });

      if (exact.length > 0)
        return exact;
      
      const scored=users
            .map(x => ({ user: x, score: this.userMatchScore(x, targetNorm) }))
            .filter(x => x.score > 0)
            .sort((a, b) => b.score - a.score);
      
      return scored.map(x => x.user);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return [];
    }
  }

  public findSuggestedCompanies(companies: CompanyDTO[], companyName: string): CompanyDTO[] {
    try {
      const tokens=this.normalizeMatchText(companyName).split(' ').filter(t => t.length > 1);

      if (!tokens.length)
        return [];

      return companies.filter(c => {
                         const normalized=this.normalizeMatchText(c.companyName ?? '');
                         return tokens.some(t => normalized.includes(t));
                       })
                       .slice(0, 3);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return [];
    }
  }

  public findSuggestedTickets(tickets: Ticket[], query: string): Ticket[] {
    try {
      const tokens=this.normalizeMatchText(query).split(' ').filter(t => t.length > 1);

      if (!tokens.length)
        return [];

      return tickets.filter(t => {
                       const values=this.ticketMatchValues(t).map(v => this.normalizeMatchText(v));
                       return tokens.some(token => values.some(v => v.includes(token)));
                     })
                     .slice(0, 3);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return [];
    }
  }

  public findSuggestedUsers(users: UserDTO[], userName: string): UserDTO[] {
    try {
      const tokens=this.normalizeMatchText(userName).split(' ').filter(t => t.length > 1);

      if (!tokens.length)
        return [];

      return users
        .map(u => {
          const fullNameCombined=`${u.firstname ?? ''} ${u.lastname ?? ''}`.trim();
          const fields          =[u.username ?? '', u.email ?? '', u.fullname ?? '', fullNameCombined].map(v => this.normalizeMatchText(v));
          const score           =tokens.reduce((acc, t) => acc + (fields.some(f => f.includes(t)) ? 1 : 0), 0);
          
          return { score, user: u };
        })
        .filter(x => x.score > 0)
        .sort((a, b) => b.score - a.score)
        .slice(0, 3)
        .map(x => x.user);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return [];
    }
  }

  public normalizeMatchText(value: string): string {
    try {
      return (value ?? '')
             .trim()
             .toLowerCase()
             .replace(/[^a-z0-9]+/g, ' ')
             .replace(/\s+/g, ' ')
             .trim();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return "";
    }
  }
  
  public parsePositiveInteger(value: string | undefined): number | undefined {
    try {
      const parsed=Number((value ?? '').toString().replace(/^#/, '').trim());
      
      return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : undefined;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return undefined;
    }
  }

  private ticketMatchValues(ticket: Ticket): string[] {
    try {
      return [String(ticket?.id    ?? ""),
              ticket?.title        ?? "",
              ticket?.iccid        ?? "",
              ticket?.requester    ?? "",
              ticket?.affectedUser ?? "",
              ticket?.company      ?? ""];
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return [];
    }
  }
  
  public uniqueSimCandidates(candidates: IdAndDescriptionDTO[]): IdAndDescriptionDTO[] {
    try {
      const seen=new Set<string>();

      const results: IdAndDescriptionDTO[]=[];

      (candidates ?? []).forEach(candidate => {
        const key=(candidate?.description ?? '').toString().trim().toLowerCase();
        
        if (!key || seen.has(key))
          return;

        seen.add(key);
        results.push(candidate);
      });
      return results;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return [];
    }
  }

  public uniqueTicketsById(tickets: Ticket[]): Ticket[] {
    try {
      const seen=new Set<number>();

      const result: Ticket[]=[];

      tickets.forEach(t => {
        const id=Number(t?.id ?? -1);
        
        if (!Number.isFinite(id) || id <= 0 || seen.has(id))
          return;

        seen.add(id);
        result.push(t);
      });

      return result;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return [];
    }
  }

  private userMatchScore(user: UserDTO, targetNorm: string): number {
    try {
      const fullNameCombined=`${user.firstname ?? ''} ${user.lastname ?? ''}`.trim();

      const values=[
        this.normalizeMatchText(user.fullname ?? ''),
        this.normalizeMatchText(fullNameCombined),
        this.normalizeMatchText(user.username ?? ''),
        this.normalizeMatchText(user.email ?? '')
      ].filter(v => v.length > 0);
  
      let best=0;

      values.forEach(value => {
        if (value===targetNorm) {
          best=Math.max(best, 500);
          return;
        }

        if (value.startsWith(targetNorm)) {
          best=Math.max(best, 320);

        } else if (value.includes(` ${targetNorm}`)) {
          best=Math.max(best, 260);

        } else if (value.includes(targetNorm)) {
          best=Math.max(best, 180);
        }
      });
  
      const hasUnderscore=(user.username ?? '').includes('_') || (user.fullname ?? '').includes('_');

      if (hasUnderscore)
        best-=20;
  
      return best;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return 0;
    }
  }

  //#endregion
}
